<template>
  <v-modal
    id="template-copy-luggage-to-days-modal"
    class-name="modal-fullscreen-sm-down"
    size="lg"
    @closed="$emit('closed')"
  >
    <modal-header>
      <modal-title>{{ $t("tourBuilder.copyLuggageModalTitle") }}</modal-title>
    </modal-header>

    <modal-body>
      <p class="text-sm text-muted mb-16">
        {{ $t("tourBuilder.copyLuggageModalIntro") }}
      </p>

      <div v-if="templateDayRows.length === 0" class="alert alert-info mb-0">
        {{ $t("tourBuilder.copyLuggageNoDays") }}
      </div>

      <template v-else>
        <div class="d-flex flex-wrap gap-8 mb-12">
          <v-button size="sm" variant="outline-primary" :disabled="submitting" class="rounded-pill" @click="selectAll">
            {{ $t("tourBuilder.copyLuggageSelectAll") }}
          </v-button>
          <v-button size="sm" variant="outline-tertiary" :disabled="submitting" class="rounded-pill" @click="selectNone">
            {{ $t("tourBuilder.copyLuggageSelectNone") }}
          </v-button>
        </div>

        <div class="border rounded-xs overflow-hidden" style="max-height: 22rem; overflow-y: auto">
          <div
            v-for="row in templateDayRows"
            :key="row.dayId"
            class="d-flex align-items-center gap-12 px-12 py-10 border-bottom"
            :class="{ 'bg-light': row.dayId === sourceActivityDayId }"
          >
            <v-checkbox
              :id="`copy-luggage-day-${row.dayId}`"
              :for-key="`copy-luggage-day-${row.dayId}`"
              :is-disabled="submitting || row.dayId === sourceActivityDayId"
              :label="row.label"
              :model-value="selected[row.dayId] === true"
              class="mb-0 flex-grow-1"
              @update:model-value="setSelected(row.dayId, $event)"
            />
          </div>
        </div>
      </template>
    </modal-body>

    <modal-footer>
      <div class="d-flex gap-8 justify-content-end w-100">
        <v-button variant="tertiary" :disabled="submitting" data-bs-dismiss="modal">
          {{ $t("general.button.cancel") }}
        </v-button>
        <v-button
          variant="primary"
          :disabled="submitting || templateDayRows.length === 0 || !hasSelection"
          @click="submit"
        >
          {{ $t("tourBuilder.copyLuggageApply") }}
        </v-button>
      </div>
    </modal-footer>
  </v-modal>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { VCheckbox } from "@uniqoders/form";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import { useUserStore } from "@/stores/user";

const props = defineProps<{
  agency: string;
  templateId: number;
  sourceTourDayItem: Record<string, any>;
}>();

const emit = defineEmits<{
  (e: "closed"): void;
  (e: "applied"): void;
}>();

const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();
const submitting = ref(false);

const selected = reactive<Record<number, boolean>>({});

const sourceActivityDayId = computed(() =>
  Number(props.sourceTourDayItem?.activityDayId ?? props.sourceTourDayItem?.tourDayId ?? 0)
);

const dayItems = (day: Record<string, any>) =>
  (day.items || day.activityDayItems || []) as Record<string, any>[];

/** All tour days; luggage can be synced to any day (creates luggage item if missing). */
const templateDayRows = computed(() => {
  const days = (userStore.currentTemplate?.tourDays || []) as Record<string, any>[];
  return days
    .map((d) => {
      const order = Number(d.order) || 0;
      const dayId = Number(d.id);
      return {
        dayId,
        order,
        label: t("tourBuilder.copyLuggageDayLabel", { day: order }),
      };
    })
    .sort((a, b) => a.order - b.order);
});

const hasSelection = computed(() =>
  Object.keys(selected).some((k) => selected[Number(k)] === true)
);

const setSelected = (dayId: number, value: boolean) => {
  if (dayId === sourceActivityDayId.value) return;
  selected[dayId] = value;
};

const selectAll = () => {
  templateDayRows.value.forEach((row) => {
    if (row.dayId !== sourceActivityDayId.value) {
      selected[row.dayId] = true;
    }
  });
};

const selectNone = () => {
  templateDayRows.value.forEach((row) => {
    selected[row.dayId] = false;
  });
};

const findLuggageItem = (day: Record<string, any>) =>
  dayItems(day).find(
    (it) => String(it.type || "").toLowerCase() === "luggage_transport" && !it.isAutoGenerated
  );

const supplierIdFromSource = (): number => {
  const src = props.sourceTourDayItem;
  return Number(src?.modelId ?? src?.model?.id ?? 0);
};

const resourcePayloadFromSource = () => {
  const src = props.sourceTourDayItem;
  const list = (src?.activityDayItemResources || []) as Record<string, any>[];
  const ids: number[] = [];
  const seen = new Set<number>();
  const obsBySupplierResourceId = new Map<number, string>();

  for (const r of list) {
    const rid = Number(
      r.supplierResourceId ?? r.supplier_resource_id ?? r.supplierResource?.id ?? 0
    );
    if (!rid) continue;
    if (!seen.has(rid)) {
      seen.add(rid);
      ids.push(rid);
    }
    if (!obsBySupplierResourceId.has(rid)) {
      obsBySupplierResourceId.set(
        rid,
        String(r.supplierObservation ?? r.supplier_observation ?? "").trim()
      );
    }
  }

  return { supplierResourceIds: ids, obsBySupplierResourceId };
};

const ensureLuggageItemId = async (activityDayId: number): Promise<number> => {
  const days = (userStore.currentTemplate?.tourDays || []) as Record<string, any>[];
  const day = days.find((d) => Number(d.id) === activityDayId);
  const existing = day ? findLuggageItem(day) : undefined;
  if (existing?.id) {
    return Number(existing.id);
  }

  const created = await api.activityService.createDayItem(props.agency, props.templateId, activityDayId, {
    type: "luggage_transport",
  });
  return Number(created.id);
};

const applyObservations = async (
  activityDayId: number,
  luggageItemId: number,
  obsBySupplierResourceId: Map<number, string>
) => {
  if (obsBySupplierResourceId.size === 0) return;

  const full = await api.activityService.retrieveActivityDayItem(
    props.agency,
    props.templateId,
    activityDayId,
    luggageItemId
  );
  const item = (full as any)?.content ?? full;
  const resources = (item?.activityDayItemResources || []) as Record<string, any>[];

  for (const r of resources) {
    const rid = Number(
      r.supplierResourceId ?? r.supplier_resource_id ?? r.supplierResource?.id ?? 0
    );
    if (!rid || !obsBySupplierResourceId.has(rid)) continue;
    const text = obsBySupplierResourceId.get(rid);
    if (text === undefined || text === "") continue;

    await api.activityService.updateActivityDayItemResource(
      props.agency,
      props.templateId,
      activityDayId,
      luggageItemId,
      Number(r.id),
      { supplier_observation: text }
    );
  }
};

const clearAllResourcesOnItem = async (
  activityDayId: number,
  luggageItemId: number
) => {
  const full = await api.activityService.retrieveActivityDayItem(
    props.agency,
    props.templateId,
    activityDayId,
    luggageItemId
  );
  const item = (full as any)?.content ?? full;
  const resources = (item?.activityDayItemResources || []) as Record<string, any>[];
  for (const r of resources) {
    await api.activityService.deleteActivityDayItemResource(
      props.agency,
      props.templateId,
      activityDayId,
      luggageItemId,
      Number(r.id)
    );
  }
};

const syncDay = async (
  activityDayId: number,
  supplierId: number,
  supplierResourceIds: number[],
  obsBySupplierResourceId: Map<number, string>
) => {
  const luggageItemId = await ensureLuggageItemId(activityDayId);

  const payload: Record<string, any> = {
    model_type: "supplier",
    model_id: supplierId,
  };
  if (supplierResourceIds.length > 0) {
    payload.supplier_resource_ids = supplierResourceIds;
  } else {
    await clearAllResourcesOnItem(activityDayId, luggageItemId);
  }

  await api.activityService.updateDayItem(
    props.agency,
    props.templateId,
    activityDayId,
    luggageItemId,
    payload
  );

  await applyObservations(activityDayId, luggageItemId, obsBySupplierResourceId);
};

const submit = async () => {
  const supplierId = supplierIdFromSource();
  if (!supplierId) return;

  const { supplierResourceIds, obsBySupplierResourceId } = resourcePayloadFromSource();
  const targets = templateDayRows.value
    .map((r) => r.dayId)
    .filter((id) => id !== sourceActivityDayId.value && selected[id] === true);

  if (targets.length === 0) return;

  submitting.value = true;
  try {
    for (const dayId of targets) {
      await syncDay(dayId, supplierId, supplierResourceIds, obsBySupplierResourceId);
    }
    toast.success(t("tourBuilder.copyLuggageSuccess"));
    emit("applied");
    emit("closed");
  } catch (e: any) {
    console.error(e);
    const msg = e?.response?.data?.message || e?.message || t("tourBuilder.copyLuggageError");
    toast.error(String(msg));
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  selectAll();
});
</script>
